import { Quote, Calendar } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import communityImage from "@/assets/concept-room-2.png";

const Community = () => {
  const { t, language } = useLanguage();
  const isDe = language === "de";

  const testimonials = [
    {
      name: "Emma Schmidt",
      role: "Designer",
      text: t("community.testimonial1"),
    },
    {
      name: "Lucas Weber",
      role: "Developer",
      text: t("community.testimonial2"),
    },
    {
      name: "Sophie Müller",
      role: "Marketing Manager",
      text: t("community.testimonial3"),
    },
  ];

  const events = [
    {
      title: { en: "Welcome Dinner", de: "Willkommens-Dinner" },
      date: { en: "Every Monday", de: "Jeden Montag" },
      description: {
        en: "Meet your new roommates over a shared meal.",
        de: "Lerne deine neuen Mitbewohner bei einem gemeinsamen Essen kennen.",
      },
    },
    {
      title: { en: "Yoga Sessions", de: "Yoga-Sessions" },
      date: { en: "Wednesdays", de: "Mittwochs" },
      description: {
        en: "Start your day with a community yoga session.",
        de: "Starte den Tag mit einer gemeinsamen Yoga-Session.",
      },
    },
    {
      title: { en: "Game Night", de: "Spieleabend" },
      date: { en: "Fridays", de: "Freitags" },
      description: {
        en: "Unwind with board games and good company.",
        de: "Entspann dich bei Brettspielen und guter Gesellschaft.",
      },
    },
  ];

  const pick = (o: { en: string; de: string }) => (isDe ? o.de : o.en);

  return (
    <div className="min-h-screen bg-sand px-4 pt-28 pb-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* Hero */}
        <div className="mb-12 text-center duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
          <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.28em] text-turkis-strong">
            {isDe ? "Community" : "Community"}
          </p>
          <h1 className="font-display text-4xl font-semibold tracking-[-0.02em] text-heading sm:text-5xl lg:text-6xl">
            {t("community.title")}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-heading/60 sm:text-xl">
            {t("community.subtitle")}
          </p>
        </div>

        {/* Community image */}
        <div className="mb-20 overflow-hidden rounded-[2rem] shadow-[0_30px_80px_-40px_rgba(15,46,40,0.45)]">
          <img
            src={communityImage}
            alt={t("public.community.metaTitle", "coliviq community")}
            className="h-[300px] w-full object-cover sm:h-[440px]"
          />
        </div>

        {/* Testimonials */}
        <div className="mb-24">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="relative rounded-3xl border border-black/5 bg-white p-8 shadow-[0_2px_20px_-12px_rgba(15,46,40,0.2)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(15,46,40,0.28)]"
              >
                <Quote className="mb-5 h-9 w-9 text-turkis-strong/25" />
                <p className="mb-7 text-[15px] italic leading-relaxed text-heading/75">
                  {testimonial.text}
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-turkis-strong/10 font-display text-lg font-semibold text-turkis-strong">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-heading">
                      {testimonial.name}
                    </p>
                    <p className="text-sm text-heading/55">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Events */}
        <div>
          <h2 className="mb-10 text-center font-display text-3xl font-semibold tracking-[-0.02em] text-heading sm:text-4xl">
            {t("community.events")}
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {events.map((event, idx) => (
              <div
                key={idx}
                className="rounded-3xl border border-black/5 bg-white p-7 shadow-[0_2px_20px_-12px_rgba(15,46,40,0.2)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(15,46,40,0.28)]"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-turkis-strong/10">
                  <Calendar
                    className="h-5 w-5 text-turkis-strong"
                    strokeWidth={1.75}
                  />
                </div>
                <h3 className="mb-1.5 font-display text-xl font-semibold text-heading">
                  {pick(event.title)}
                </h3>
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-turkis-strong">
                  {pick(event.date)}
                </p>
                <p className="text-[15px] leading-relaxed text-heading/60">
                  {pick(event.description)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Community;
